import React, { useEffect, useState } from 'react';
import Table from '../table/Table';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { get } from '../../api';
import convertToQueryParams from '../../utils/convertToQueryParams';
import useFetch from '../../utils/useFetch';
import useFilters from '../../hooks/useFilters';
import Pagination from '../pagination/Pagination';
import { useSearchParams } from 'react-router-dom';

const MyOrders = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const Api = `sales/order-summaries/`;

	// Use useSearchParams to read and update URL query parameters
	const [searchParams, setSearchParams] = useSearchParams();

	// Initialize filters from URL or use defaults
	const initialFilters = {
		ordering: searchParams.get('ordering') || null,
		is_active: searchParams.get('is_active') || null,
		page: parseInt(searchParams.get('page')) || 1,
		size: parseInt(searchParams.get('size')) || 10,
		search: searchParams.get('search') || null,
		tags: searchParams.get('tags') || null,
		status: searchParams.get('status') || null,
	};

	const {
		filters,
		setFilters,
	} = useFilters(initialFilters);

	const { data, loading, error, headers } = useFetch(`${Api}${convertToQueryParams(filters)}`);

	// Local state for the search input value
	const [searchInput, setSearchInput] = useState(filters.search || '');

	// Function to update URL when filters change
	const updateURL = (newFilters) => {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(newFilters)) {
			if (value) {
				params.set(key, value);
			} else {
				params.delete(key);
			}
		}
		setSearchParams(params);
	};

	const onPageChange = (page) => {
		const newFilters = { ...filters, page };
		setFilters(newFilters);
		updateURL(newFilters); // Update URL
	};

	const handleSort = (column) => {
		let ordering = column;
		if (filters.ordering === column) {
			ordering = `-${column}`;
		} else if (filters.ordering === `-${column}`) {
			ordering = null;
		}
		const newFilters = { ...filters, ordering, page: 1 };
		setFilters(newFilters);
		updateURL(newFilters); // Update URL
	};

	const handleStatusFilter = (status) => {
		const newFilters = { ...filters, status, page: 1 };
		setFilters(newFilters);
		updateURL(newFilters); // Update URL
	};

	// Function to clear filters when clicking on the "Total Order" card
	const clearFilters = () => {
		const newFilters = {
			ordering: null,
			is_active: null,
			page: 1,
			size: 10,
			search: null,
			tags: null,
			status: null,
			days: 30,
		};
		setFilters(newFilters);
		setSearchInput(''); // Clear the search input
		updateURL(newFilters); // Update URL
	};

	// Function to handle search input changes
	const handleSearchInputChange = (event) => {
		setSearchInput(event.target.value); // Update local state only
	};

	// Function to trigger search when the button is clicked
	const handleSearchClick = () => {
		const newFilters = { ...filters, search: searchInput, page: 1 }; // Reset to page 1 when searching
		setFilters(newFilters);
		updateURL(newFilters); // Update URL
	};

	return (
		<div className="overflow-hidden">
			<h2 className="text-xl text-black font-semibold mb-5">My Orders</h2>
			<div className="mb-5 flex gap-2">
				<input
					type="text"
					placeholder="Search orders..."
					value={searchInput}
					onChange={handleSearchInputChange}
					className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
				/>
				<button
					onClick={handleSearchClick}
					className="p-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
				>
					Search
				</button>
			</div>

			<Table
				title="Recent Orders"
				data={data}
				loading={loading}
				error={error}
				onSort={handleSort}
				sortColumn={filters.ordering}
			/>
			<Pagination
				className="pagination-bar"
				currentPage={filters.page}
				totalCount={data?.count || 0}
				pageSize={filters.size}
				onPageChange={(page) => onPageChange(page)}
			/>
		</div>
	);
};

export default MyOrders;
